import React from "react";
import { View, TouchableOpacity, StyleSheet } from "react-native";
import Text from "@components/CustomText";
import StatusBadge from "./StatusBadge";
import { tokens, sharedStyles } from "../styles";

// Crate card — derived from data.CRATE entries.
// Mirrors the prototype's CrateCard hierarchy: title, status pills, KPIs,
// customer pills inline, View Map at right.

const packingState = (card) => {
  const packed = Number(card?.packed_tps) || 0;
  const total = Number(card?.tp_count) || 0;
  if (total > 0 && packed >= total) {
    return { label: "FULLY PACKED", state: "packed" };
  }
  if (packed <= 0) return { label: "UNPACKED", state: "unpacked" };
  return { label: `PARTIAL ${packed}/${total}`, state: "partial" };
};

// Crate-level "ASSIGNED TO" surfaces DBs whose customers are actually
// in this crate (via cp_db_assigned_touchpoints, reflected on tp.cp_db_id).
// Multi-DB locality subscription does NOT apply at the crate level — a
// customer is assigned to exactly one DB at a time, and the crate's
// chip list should show only those DBs. (Locality cards do the wider
// cp_dbs[*].routes cross-reference because the locality serves all
// subscribed DBs even if no customer is yet on them.)
const collectAssignedDbs = (tpList) => {
  const seen = new Map();
  (tpList || []).forEach((tp) => {
    const dbId = tp?.cp_db_id;
    if (!dbId || dbId === "MISROUTE" || dbId === "UNASSIGN") return;
    if (tp?.status && tp.status !== "ASSIGNED") return;
    if (!seen.has(String(dbId))) {
      seen.set(String(dbId), {
        cp_db_id: dbId,
        db_name: tp?.db_name || `DB ${dbId}`,
        count: 1,
      });
    } else {
      seen.get(String(dbId)).count += 1;
    }
  });
  return Array.from(seen.values());
};

const CrateCard = ({ card, onPressViewMap }) => {
  const pack = packingState(card);
  const hasMarkers =
    Array.isArray(card?.tp_markers) && card.tp_markers.length > 0;
  const tpList = Array.isArray(card?.tp_list) ? card.tp_list : [];
  const unassignedSet = new Set(
    (Array.isArray(card?.unassigned_tp_list)
      ? card.unassigned_tp_list
      : []
    ).map(String),
  );
  const assignedDbs = collectAssignedDbs(tpList);

  return (
    <View style={sharedStyles.card}>
      <View style={styles.titleRow}>
        <Text variant="title_16" style={styles.title} numberOfLines={1}>
          {card?.title || "—"}
        </Text>
        {hasMarkers && onPressViewMap ? (
          <TouchableOpacity
            activeOpacity={0.85}
            onPress={() => onPressViewMap(card.tp_markers)}
            style={styles.mapBtn}
          >
            <Text variant="caption_2" style={styles.mapBtnText}>
              View Map
            </Text>
          </TouchableOpacity>
        ) : null}
      </View>
      <View style={styles.pillsRow}>
        <StatusBadge
          label={card?.is_rescheduled ? "Rescheduled" : "New"}
          state={card?.is_rescheduled ? "rescheduled" : "new"}
        />
        {pack ? <StatusBadge label={pack.label} state={pack.state} /> : null}
        {card?.crate_size ? (
          <StatusBadge label={card.crate_size} state="neutral" />
        ) : null}
      </View>
      <View style={styles.kpiRow}>
        <Text variant="caption_2" style={styles.kpi}>
          TPs:{" "}
          <Text variant="caption_2" style={styles.kpiValue}>
            {card?.tp_count ?? 0}
          </Text>
        </Text>
        {card?.connecting_large_crate_count != null ? (
          <Text variant="caption_2" style={styles.kpi}>
            {"   "}Conn-L:{" "}
            <Text variant="caption_2" style={styles.kpiValue}>
              {card.connecting_large_crate_count}
            </Text>
          </Text>
        ) : null}
        {card?.connecting_small_crate_count != null ? (
          <Text variant="caption_2" style={styles.kpi}>
            {"   "}Conn-S:{" "}
            <Text variant="caption_2" style={styles.kpiValue}>
              {card.connecting_small_crate_count}
            </Text>
          </Text>
        ) : null}
      </View>
      {assignedDbs.length > 0 ? (
        <View style={styles.dbsBlock}>
          <Text variant="caption_2" style={styles.dbsLabel}>
            Assigned to
          </Text>
          <View style={styles.dbsRow}>
            {assignedDbs.map((d) => (
              <StatusBadge
                key={String(d.cp_db_id)}
                label={`${d.db_name} · ${d.count}`}
                state="preferred"
              />
            ))}
          </View>
        </View>
      ) : null}
      {tpList.length > 0 ? (
        <View style={styles.cxBlock}>
          <Text variant="caption_2" style={styles.cxLabel}>
            Customers ({tpList.length})
          </Text>
          <View style={styles.cxPillsRow}>
            {tpList.slice(0, 8).map((tp) => {
              const isUnassigned = unassignedSet.has(String(tp?.user_id));
              return (
                <StatusBadge
                  key={String(tp?.user_id)}
                  label={tp?.user_name || "—"}
                  state={isUnassigned ? "unpacked" : "neutral"}
                />
              );
            })}
            {tpList.length > 8 ? (
              <Text variant="caption_2" style={styles.more}>
                +{tpList.length - 8} more
              </Text>
            ) : null}
          </View>
        </View>
      ) : null}
    </View>
  );
};

export default CrateCard;

const styles = StyleSheet.create({
  titleRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: tokens.spacing.small,
  },
  title: {
    color: tokens.textPrimary,
    fontWeight: "700",
    flex: 1,
    marginRight: tokens.spacing.small,
  },
  mapBtn: {
    paddingHorizontal: tokens.spacing.medium,
    paddingVertical: 6,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: tokens.borderMedium,
  },
  mapBtnText: { color: tokens.textPrimary, fontSize: 12, fontWeight: "600" },
  pillsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginBottom: tokens.spacing.small,
  },
  kpiRow: { flexDirection: "row", flexWrap: "wrap" },
  kpi: { color: tokens.textSecondary, fontSize: 12 },
  kpiValue: { color: tokens.textPrimary, fontWeight: "700" },
  dbsBlock: { marginTop: tokens.spacing.medium },
  dbsLabel: {
    color: tokens.textSecondary,
    fontSize: 11,
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: 0.4,
    marginBottom: tokens.spacing.tiny,
  },
  dbsRow: { flexDirection: "row", flexWrap: "wrap" },
  cxBlock: { marginTop: tokens.spacing.medium },
  cxLabel: {
    color: tokens.textSecondary,
    fontSize: 11,
    fontWeight: "600",
    marginBottom: tokens.spacing.small,
  },
  cxPillsRow: { flexDirection: "row", flexWrap: "wrap" },
  more: {
    color: tokens.textTertiary,
    fontSize: 12,
    alignSelf: "center",
    marginLeft: 4,
  },
});
